import { HDate } from '@hebcal/core';
import { DATETIME_OPTIONS, DATE_OPTIONS, DAYS_IN_HEBREW, DAY_NAMES, HEBREW_MONTHS, MONTHS_IN_HEBREW } from '@/utils/const';
import { formatHebrewYear } from '@/utils/hebrew_year';
import { toHebrewPickerDateInputValue, toWallClockDate } from '@/utils/wall_clock_date';

const filters = {
  firstLetter(value: string) {
    return value != null ? value[0] : '';
  },

  capitalize(value: string) {
    return value != null ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  },

  uppercase(value: string) {
    return value != null ? value.toString().toUpperCase() : '';
  },

  getDateOnly(value: string) {
    return value != null
      ? new Date(value).toLocaleDateString('en-US', DATE_OPTIONS)
      : '-';
  },

  getTimeOnly(value: string) {
    return value != null
      ? new Date(value).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false, // Use 24-hour format, removes AM/PM
        })
      : '-';
  },

  getTimeOnly12Hrs(value: string) {
    if (!value) return '-';
    const date = new Date(value);
    // Use toLocaleTimeString to get local time in 12-hour format, no leading zero, no AM/PM
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).replace(/ AM| PM/i, '');
  },

  convertToHebrewDate(value: string) {
    const gregDate = toWallClockDate(value);
    if (!gregDate) return '-';
    const hebrewDate = new HDate(gregDate);
      let monthIndex = HEBREW_MONTHS.indexOf(hebrewDate.getMonthName())
      if (monthIndex != -1) {
          return `${DAYS_IN_HEBREW[hebrewDate.getDate() - 1]} ${MONTHS_IN_HEBREW[monthIndex]} ${formatHebrewYear(hebrewDate.getFullYear())}`
      }
      return '-'

      // return hebrewDate.toString();
  },

  // Gregorian counterpart of convertToHebrewDate: the "Display Calendar Type"
  // setting swaps between the two. Reads the stored date as a wall-clock value
  // (same as the Hebrew label) so both calendars describe the very same day,
  // and returns '-' for empty/invalid input to match convertToHebrewDate.
  convertToGregorianDate(value: string | Date) {
    const date = toWallClockDate(value);
    if (!date) return '-';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  },

  convertToHebrewDateObject(value: string) {
    const gregDate = new Date(value);
    const hebrewDate = new HDate(gregDate);
      let monthIndex = HEBREW_MONTHS.indexOf(hebrewDate.getMonthName())
      if (monthIndex != -1) {
          return {
            dayOfWeek: DAY_NAMES[gregDate.getDay()],
            dayOfMonth: DAYS_IN_HEBREW[hebrewDate.getDate() - 1],
            month: MONTHS_IN_HEBREW[monthIndex],
            year: formatHebrewYear(hebrewDate.getFullYear())
          }
      }
      return '-'

      // return hebrewDate.toString();
  },

  convertToHebrewDateDayAndMonth(value: string) {
      const gregDate = new Date(value);
      const hebrewDate = new HDate(gregDate);
      return `${hebrewDate.getDate()} ${HEBREW_MONTHS[hebrewDate.getMonth() - 1]}`;
  },

  convertToHebrewMonthOnly(value: string) {
    const gregDate = new Date(value);
    const hebrewDate = new HDate(gregDate);
    return `${HEBREW_MONTHS[hebrewDate.getMonth() - 1]}`;
  },

  convertHDateToGregorian(hDate: HDate) {
      // Keep this as a calendar day rather than converting local midnight to
      // UTC, which can silently send the previous/next day to the backend.
      return toHebrewPickerDateInputValue(hDate);
  },

  convertHDateStringToGregorian(hebrewDateStr: String) {
      const [day, month, year] = hebrewDateStr.split(' ');
      const hebrewDate = new HDate(parseInt(day), month, parseInt(year));

      const gregorianDate = hebrewDate.greg();

      return gregorianDate;
  },


  getTimeOnly24Hrs(value: string) {
    return value != null
      ? new Date(value).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
      : '-';
  },

  getDateAndTime(value: string) {
    return value != null
      ? new Date(value).toLocaleDateString('en-US', DATETIME_OPTIONS)
      : '-';
  },

  checkDateRange(startDate: Date, endDate: Date): number {
    const now = new Date();
    if (now > endDate) {
      return 0; // Past
    } else if (now > startDate && now < endDate) {
      return 1; // Within the range
    } else if (now < startDate) {
      return 0;
    } else {
      return 0;
    }
  },

  formatDate(value: string) {
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  },

  round(value: number) {
    return value.toFixed(2);
  },

  capitalizedFirstLetter(str: string) {
    if (!str) return '';
    return str.charAt(0).toUpperCase();
  },

  formatNumberWithComma(number: number) {
    if (isNaN(number)) {
      return 'Invalid number';
    }
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },
};

export default filters;
